import React from "react";
import {
  Droplets,
  ShieldCheck,
  Home,
  Building2,
  Layers,
  Wrench
} from "lucide-react";
import { Helmet } from "react-helmet-async"; // SEO Added
import wp10 from "../assets/waterproofing10.jpg";

const WaterproofingAboutSection = () => {
  
  // SEO Schema for Local Service
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Waterproofing",
    "provider": {
      "@type": "HomeAndConstructionBusiness",
      "name": "Luxuriya Waterproofing",
      "image": "https://www.luxuriyahomes.com/assets/waterproofing10.jpg",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Noida",
        "addressRegion": "UP",
        "addressCountry": "IN"
      },
      "telephone": "+91-7291832000"
    },
    "areaServed": ["Delhi", "Noida", "Gurgaon", "Ghaziabad", "Faridabad"],
    "description": "Professional waterproofing services for terraces, basements, and walls using ISO certified materials."
  };

  return (
    <div className="bg-[#f7f7f7]">
      
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Best Waterproofing Services in Delhi, Noida & Gurgaon | Luxuriya Homes</title>
        <meta 
          name="description" 
          content="Expert waterproofing solutions for terraces, basements, and bathrooms in Delhi NCR. We use advanced coating techniques to stop leakage and seepage permanently." 
        />
        <meta name="keywords" content="waterproofing contractors delhi, roof leakage repair noida, basement waterproofing gurgaon, wall seepage solution" />
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      {/* INTRO */}
      <section id="waterproofing-about-section" className="py-24">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <div>
             <h4 className="text-amber-600 font-bold text-xs uppercase tracking-widest mb-3">Luxuriya Waterproofing</h4>
            <h2 id="identity-heading" className="text-3xl md:text-5xl font-serif mb-6 leading-tight text-neutral-900">
            Complete Protection from <br/>Water<span className="italic text-gray-500"> Damage.</span>
            </h2>

            <p className="text-gray-600 leading-relaxed mb-5 text-lg">
              Luxuriya Waterproofing is a specialized division of Luxuriya Homes,
              offering advanced waterproofing solutions for villas, apartments,
              commercial spaces and high-rise buildings.
            </p>

            <p className="text-gray-600 leading-relaxed text-lg">
              Our goal is to ensure your property remains safe from seepage,
              dampness, leakage and long-term structural damage.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-6">
              <div>
                <h3 className="text-2xl font-bold text-zinc-900">500+</h3>
                <p className="text-sm text-gray-500">Projects</p>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-zinc-900">10+</h3>
                <p className="text-sm text-gray-500">Years</p>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-zinc-900">100%</h3>
                <p className="text-sm text-gray-500">Satisfaction</p>
              </div>
            </div>
          </div>

          <div>
            <img 
              src={wp10}
              alt="Professional Waterproofing Service Team in Delhi"
              className="rounded-2xl shadow-xl w-full h-[420px] object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-4xl font-serif font-bold text-center mb-16 text-zinc-900">
            Our Waterproofing Services
          </h3>

          <div className="grid md:grid-cols-3 gap-10">
            {[
              { icon: <Home />, title: "Terrace & Roof" },
              { icon: <Building2 />, title: "Basement" },
              { icon: <Droplets />, title: "Bathroom" },
              { icon: <Layers />, title: "Crack Repair" },
              { icon: <ShieldCheck />, title: "Roof Coating" },
              { icon: <Wrench />, title: "Leak Detection" },
            ].map((item, i) => (
              <div key={i} className="p-8 rounded-2xl shadow-md border border-gray-100 hover:shadow-xl transition text-center bg-white group">
                <div className="w-12 h-12 mx-auto mb-4 text-amber-600 group-hover:scale-110 transition-transform">
                  {item.icon}
                </div>
                <h4 className="font-semibold text-lg text-zinc-800">{item.title}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-24 bg-[#f7f7f7]">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <h3 className="text-4xl font-serif font-bold mb-6 text-zinc-900">
              Why Choose Luxuriya Waterproofing?
            </h3>
            <ul className="space-y-4 text-gray-600 text-lg">
              <li className="flex items-center gap-2"><span className="text-amber-600">✔</span> ISO certified materials</li>
              <li className="flex items-center gap-2"><span className="text-amber-600">✔</span> Trained & experienced professionals</li>
              <li className="flex items-center gap-2"><span className="text-amber-600">✔</span> Long-term waterproofing warranty</li>
              <li className="flex items-center gap-2"><span className="text-amber-600">✔</span> Advanced German & Asian techniques</li>
              <li className="flex items-center gap-2"><span className="text-amber-600">✔</span> Affordable & transparent pricing</li>
            </ul>
          </div>

          <div className="bg-white rounded-2xl p-10 shadow-xl border border-zinc-100">
            <h4 className="font-semibold text-xl mb-4 text-amber-600">
              Our Promise
            </h4>
            <p className="text-gray-600 leading-relaxed">
              We don’t just fix leaks — we build permanent protection
              systems that increase the life of your property and
              enhance its overall value.
            </p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default WaterproofingAboutSection;